'use client';

import type { ForwardRefRenderFunction, InputHTMLAttributes } from 'react';
import { Icon } from '../../../core-components';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { twJoin } from 'tailwind-merge';
import { twMerge } from '@tuic/tailwind-config';

type BaseProps = {
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean | 'indeterminate';
  invalid?: string;

  on?: 'white' | 'gray';
};

export type CheckboxProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseProps | 'type'>;

const CheckboxRenderFunction: ForwardRefRenderFunction<
  HTMLInputElement | null,
  CheckboxProps
> = (
  { checked, defaultChecked, invalid, on = 'gray', ...rest },
  forwardedRef,
) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const checkedProp = checked === 'indeterminate' ? false : checked;
  const defaultCheckedProp =
    defaultChecked === 'indeterminate' ? false : defaultChecked;

  const comparableChecked =
    typeof checked !== 'undefined' ? checked : defaultChecked;

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => ref.current,
  );

  useEffect(() => {
    if (comparableChecked !== 'indeterminate') {
      return;
    }

    if (!ref || !('current' in ref) || !ref.current) {
      return;
    }

    ref.current.indeterminate = true;
  }, [ref, comparableChecked]);

  useEffect(() => {
    ref.current?.setCustomValidity(invalid ?? '');
  }, [ref, invalid]);

  const iconWrapperClassName = 'hidden absolute inset-0 pointer-events-none';
  const iconClassName = 'w-full h-full';

  return (
    <span className="relative inline-block h-8 w-8 flex-shrink-0 md:h-6 md:w-6">
      <input
        {...rest}
        className={twMerge(
          'peer absolute inset-0 h-8 w-8 appearance-none rounded-xs bg-surface-white md:h-6 md:w-6',
          'before:absolute before:inset-0 before:rounded-xs before:border before:border-stroke-primary-100',
          'checked:before:border-transparent checked:before:bg-surface-primary-100',
          'indeterminate:before:border-transparent indeterminate:before:bg-surface-primary-100',
          'invalid:before:border-stroke-error-100',
          'hover:cursor-pointer hover:before:border-4',
          'focus:outline-none focus-visible:shadow-focus-state',
          on === 'white' &&
            'disabled:before:border-stroke-secondary-40 disabled:hover:cursor-not-allowed disabled:hover:bg-transparent',
          on === 'gray' &&
            'disabled:checked:before:bg-surface-white disabled:hover:before:border disabled:hover:cursor-not-allowed disabled:bg-surface-secondary-7 disabled:hover:bg-transparent',
        )}
        type="checkbox"
        defaultChecked={defaultCheckedProp}
        checked={checkedProp}
        ref={ref}
      />
      <span
        className={`${iconWrapperClassName} text-white peer-indeterminate:inline-block`}
      >
        <Icon name="minus" size="md" className={iconClassName} />
      </span>
      <span
        className={`${iconWrapperClassName} text-marine-high-emphasis peer-checked:inline-block`}
      >
        <Icon name="check" size="md" className={iconClassName} />
      </span>
    </span>
  );
};

/**
 * Checkboxes let users select one or more items from a list, or turn an item on or off.
 *
 * ## Indeterminate (Partial) Checkboxes
 *
 * To set a checkbox to an indeterminate state, provide `indeterminate` as the value of `defaultChecked` or `checked`. Keep in mind, that technically the checkbox is unchecked while `indeterminate`.
 *
 * ```tsx
 * <Checkbox defaultChecked="indeterminate" />
 * ```
 *
 * ## Invalid Checkboxes (Checkboxes with Errors)
 *
 * Provide an error message in the `invalid` attribute to mark the checkbox as errored.
 *
 * ```tsx
 * <Checkbox invalid="You need to accept our ToS." />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement | null, CheckboxProps>(
  CheckboxRenderFunction,
);
